import type { ReactNode } from 'react'
import type { PaymentMethod, PaymentMethods } from '@/types'

import { CreditCard, Landmark } from 'lucide-react'
import { useT } from '@/lib/i18n'
import { cn } from '@/lib/utils'

interface PaymentMethodPickerProps {
    methods: PaymentMethods
    value: PaymentMethod
    onChange: (method: PaymentMethod) => void
    name: string
}

/** Picks the option the buyer chose, or the first one that is switched on when their choice is not available. */
export const resolveMethod = (methods: PaymentMethods, chosen: PaymentMethod): PaymentMethod => {
    if (methods[chosen]) return chosen

    return methods.card ? 'card' : 'eft'
}

export const PaymentMethodPicker = ({ methods, value, onChange, name }: PaymentMethodPickerProps): ReactNode => {
    const t = useT()

    const options: Array<{ key: PaymentMethod, label: string, hint: string, icon: typeof CreditCard }> = [
        { key: 'card', label: t('payByCard'), hint: t('payByCardHint'), icon: CreditCard },
        { key: 'eft', label: t('payByEft'), hint: t('payByEftHint'), icon: Landmark }
    ]

    const available = options.filter(option => methods[option.key])

    if (available.length === 0) return null

    return (
        <fieldset className='space-y-2'>
            <legend className='mb-2 font-bold'>{t('paymentMethod')}</legend>

            {available.map(option => (
                <label
                    key={option.key}
                    className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-xl border-2 border-line p-3 transition-colors',
                        value === option.key ? 'bg-sun text-ink' : 'bg-card'
                    )}
                >
                    <input
                        type='radio'
                        name={name}
                        value={option.key}
                        checked={value === option.key}
                        onChange={() => onChange(option.key)}
                        className='mt-1 size-4 accent-pink'
                    />

                    <span className='min-w-0'>
                        <span className='flex items-center gap-2 font-bold'>
                            <option.icon className='size-4' />
                            {option.label}
                        </span>

                        <span className={cn('mt-0.5 block text-xs', value === option.key ? 'text-ink/80' : 'text-muted-foreground')}>{option.hint}</span>
                    </span>
                </label>
            ))}
        </fieldset>
    )
}
